import { computed } from "vue";
import { useI18n } from "vue-i18n";
import {
    arrayField,
    buttonField,
    createBlock,
    descriptiveImageField,
    headingField,
    htmlField,
    textField,
} from "./blockBuilder";

import type { BlockDef } from "./block.interfaces";
import { useDefaultColors } from "./useDefaultColors";
import {
    PanelWithAppScreenshot,
    CenteredOnPanel,
    SimpleCentered,
    SimpleJustified,
    SplitWithImage,
    SplitWithImageRight,
    TwoColumnsWithPhoto,
    TwoColumnsWithPhotoRight,
    type TwoColumnsWithPhotoProps,
    PanelWithAppScreenshotProps,
    CenteredOnPanelProps,
    SimpleCenteredProps,
    SimpleJustifiedProps,
} from "@/views/blocks/cta/cta";
import { getPaceholderImageUrl } from "@/apps/page/services/page.service";
import { useGetters } from "@/store/vuex-composition-mappers";
import {
    SplitWithImageProps,
    SplitWithImageRightProps,
    TwoColumnsWithPhotoRightProps,
} from "@/views/blocks/cta/cta.interfaces";
import { useFieldConfig } from "./useFieldConfig";

export function useCtas() {
    const { t } = useI18n();
    const { colorDefaults } = useDefaultColors();
    // @ts-expect-error TODO: Fix typing in the future
    const { pageColors } = useGetters("page", ["pageColors"]);
    const { translations } = useFieldConfig();

    const panelWithAppScreenshotBlock = computed<BlockDef>(() =>
        createBlock<PanelWithAppScreenshotProps>({
            typeId: "PanelWithAppScreenshot",
            type: "cta",
            name: t("page.blocks.cta.panelWithAppScreenshot"),
            component: PanelWithAppScreenshot,
        })
            .withField(
                "tag",
                textField(translations.value.tag, "Advanced Technology", translations.value.text)
            )
            .withField(
                "title",
                headingField(translations.value.title, "Transform Business with Easytools", "h2", {
                    text: translations.value.text,
                    tag: translations.value.tag,
                })
            )
            .withField(
                "description",
                htmlField(
                    translations.value.description,
                    "Our platform simplifies complex workflows and transforms your business operations. Experience seamless integration today.",
                    translations.value.description
                )
            )
            .withField(
                "primaryButton",
                buttonField(
                    translations.value.primaryButton,
                    {
                        label: "Get started",
                        href: "https://cart.easy.tools/register",
                    },
                    {
                        label: translations.value.label,
                        href: translations.value.href,
                    }
                )
            )
            .withField(
                "secondaryButton",
                buttonField(
                    translations.value.secondaryButton,
                    {
                        label: "Learn more",
                        href: "https://www.easy.tools/changelog/easytools-quick-start",
                    },
                    {
                        label: translations.value.label,
                        href: translations.value.href,
                    }
                )
            )
            .withField(
                "image",
                descriptiveImageField(
                    translations.value.src,
                    {
                        src: getPaceholderImageUrl("760/760", pageColors.value.brand),
                        alt: "Cta image",
                    },
                    { src: translations.value.src, alt: translations.value.alt }
                )
            )
            .withStyleFields([
                colorDefaults.value.background,
                colorDefaults.value.text,
                colorDefaults.value.brand,
                colorDefaults.value.border,
            ])
            .build()
    );

    const centeredOnPanelBlock = computed<BlockDef>(() =>
        createBlock<CenteredOnPanelProps>({
            typeId: "CenteredOnPanel",
            type: "cta",
            name: t("page.blocks.cta.centeredOnPanel"),
            component: CenteredOnPanel,
        })
            .withField(
                "tag",
                textField(
                    translations.value.tag,
                    "Transform your workflow",
                    translations.value.text
                )
            )
            .withField(
                "title",
                headingField(translations.value.title, "Ready to transform your workflow?", "h2", {
                    text: translations.value.text,
                    tag: translations.value.tag,
                })
            )
            .withField(
                "description",
                htmlField(
                    translations.value.description,
                    "Join thousands of teams who have already revolutionized their workflow with our platform. Start your journey today.",
                    translations.value.description
                )
            )
            .withField(
                "primaryButton",
                buttonField(
                    translations.value.primaryButton,
                    {
                        label: "Get started",
                        href: "#",
                    },
                    {
                        label: translations.value.label,
                        href: translations.value.href,
                    }
                )
            )
            .withField(
                "secondaryButton",
                buttonField(
                    translations.value.secondaryButton,
                    {
                        label: "Learn more",
                        href: "#",
                    },
                    {
                        label: translations.value.label,
                        href: translations.value.href,
                    }
                )
            )
            .withStyleFields([
                colorDefaults.value.background,
                colorDefaults.value.text,
                colorDefaults.value.brand,
                colorDefaults.value.border,
            ])
            .build()
    );

    const simpleCenteredBlock = computed<BlockDef>(() =>
        createBlock<SimpleCenteredProps>({
            typeId: "SimpleCentered",
            type: "cta",
            name: t("page.blocks.cta.simpleCentered"),
            component: SimpleCentered,
        })
            .withField(
                "tag",
                textField(translations.value.tag, "Ready to get started?", translations.value.text)
            )
            .withField(
                "title",
                headingField(translations.value.title, "Start your free trial today", "h2", {
                    text: translations.value.text,
                    tag: translations.value.tag,
                })
            )
            .withField(
                "description",
                htmlField(
                    translations.value.description,
                    "Join thousands of satisfied users who have already discovered the power of our platform. Experience the difference for yourself with our risk-free trial.",
                    translations.value.description
                )
            )
            .withField(
                "primaryButton",
                buttonField(
                    translations.value.primaryButton,
                    {
                        label: "Get started",
                        href: "#",
                    },
                    {
                        label: translations.value.label,
                        href: translations.value.href,
                    }
                )
            )
            .withField(
                "secondaryButton",
                buttonField(
                    translations.value.secondaryButton,
                    {
                        label: "Learn more",
                        href: "#",
                    },
                    {
                        label: translations.value.label,
                        href: translations.value.href,
                    }
                )
            )
            .withStyleFields([
                colorDefaults.value.background,
                colorDefaults.value.text,
                colorDefaults.value.brand,
                colorDefaults.value.border,
            ])
            .build()
    );

    const simpleJustifiedBlock = computed<BlockDef>(() =>
        createBlock<SimpleJustifiedProps>({
            typeId: "SimpleJustified",
            type: "cta",
            name: t("page.blocks.cta.simpleJustified"),
            component: SimpleJustified,
        })
            .withField(
                "tag",
                textField(translations.value.tag, "Ready to dive in?", translations.value.text)
            )
            .withField(
                "title",
                headingField(translations.value.title, "Start your free trial today.", "h2", {
                    text: translations.value.text,
                    tag: translations.value.tag,
                })
            )
            .withField(
                "description",
                htmlField(
                    translations.value.description,
                    "Experience the power of our platform with a no-commitment trial. Get full access to all features and see how we can transform your workflow.",
                    translations.value.description
                )
            )
            .withField(
                "primaryButton",
                buttonField(
                    translations.value.primaryButton,
                    {
                        label: "Get started",
                        href: "#",
                    },
                    {
                        label: translations.value.label,
                        href: translations.value.href,
                    }
                )
            )
            .withField(
                "secondaryButton",
                buttonField(
                    translations.value.secondaryButton,
                    {
                        label: "Learn more",
                        href: "#",
                    },
                    {
                        label: translations.value.label,
                        href: translations.value.href,
                    }
                )
            )
            .withStyleFields([
                colorDefaults.value.background,
                colorDefaults.value.text,
                colorDefaults.value.brand,
                colorDefaults.value.border,
            ])
            .build()
    );

    const splitWithImageBlock = computed<BlockDef>(() =>
        createBlock<SplitWithImageProps>({
            typeId: "SplitWithImage",
            type: "cta",
            name: t("page.blocks.cta.splitWithImage"),
            component: SplitWithImage,
        })
            .withField(
                "tag",
                textField(
                    translations.value.tag,
                    "Transform Your Business",
                    translations.value.text
                )
            )
            .withField(
                "title",
                headingField(translations.value.title, "Elevate Your Workflow Today", "h2", {
                    text: translations.value.text,
                    tag: translations.value.tag,
                })
            )
            .withField(
                "description",
                htmlField(
                    translations.value.description,
                    "Join thousands of satisfied customers who have already revolutionized their business processes with our comprehensive suite of tools. Our platform seamlessly integrates with your existing workflows, providing immediate productivity gains and measurable ROI.",
                    translations.value.description
                )
            )
            .withField(
                "primaryButton",
                buttonField(
                    translations.value.primaryButton,
                    {
                        label: "Start Free Trial",
                        href: "#",
                    },
                    {
                        label: translations.value.label,
                        href: translations.value.href,
                    }
                )
            )
            .withField(
                "image",
                descriptiveImageField(
                    translations.value.src,
                    {
                        src: getPaceholderImageUrl("760/760", pageColors.value.brand),
                        alt: "Modern workspace with laptop and plants",
                    },
                    {
                        src: translations.value.src,
                        alt: translations.value.alt,
                    }
                )
            )
            .withStyleFields([
                colorDefaults.value.background,
                colorDefaults.value.text,
                colorDefaults.value.brand,
                colorDefaults.value.border,
            ])
            .build()
    );

    const splitWithImageRightBlock = computed<BlockDef>(() =>
        createBlock<SplitWithImageRightProps>({
            typeId: "SplitWithImageRight",
            type: "cta",
            name: t("page.blocks.cta.splitWithImageRight"),
            component: SplitWithImageRight,
        })
            .withField(
                "tag",
                textField(
                    translations.value.tag,
                    "Transform Your Business",
                    translations.value.text
                )
            )
            .withField(
                "title",
                headingField(translations.value.title, "Elevate Your Workflow Today", "h2", {
                    text: translations.value.text,
                    tag: translations.value.tag,
                })
            )
            .withField(
                "description",
                htmlField(
                    translations.value.description,
                    "Join thousands of satisfied customers who have already revolutionized their business processes with our comprehensive suite of tools. Our platform seamlessly integrates with your existing workflows, providing immediate productivity gains and measurable ROI.",
                    translations.value.description
                )
            )
            .withField(
                "primaryButton",
                buttonField(
                    translations.value.primaryButton,
                    {
                        label: "Start Free Trial",
                        href: "#",
                    },
                    {
                        label: translations.value.label,
                        href: translations.value.href,
                    }
                )
            )
            .withField(
                "image",
                descriptiveImageField(
                    translations.value.src,
                    {
                        src: getPaceholderImageUrl("760/760", pageColors.value.brand),
                        alt: "Modern workspace with laptop and plants",
                    },
                    {
                        src: translations.value.src,
                        alt: translations.value.alt,
                    }
                )
            )
            .withStyleFields([
                colorDefaults.value.background,
                colorDefaults.value.text,
                colorDefaults.value.brand,
                colorDefaults.value.border,
            ])
            .build()
    );

    const twoColumnsWithPhotoBlock = computed<BlockDef>(() =>
        createBlock<TwoColumnsWithPhotoProps>({
            typeId: "TwoColumnsWithPhoto",
            type: "cta",
            name: t("page.blocks.cta.twoColumnsWithPhoto"),
            component: TwoColumnsWithPhoto,
        })
            .withField(
                "title",
                headingField(translations.value.title, "Become part of our team", "h2", {
                    text: translations.value.text,
                    tag: translations.value.tag,
                })
            )
            .withField(
                "description",
                htmlField(
                    translations.value.description,
                    "We provide exceptional perks and a supportive workplace designed to foster your professional development.",
                    translations.value.description
                )
            )
            .withArrayField(
                "listItems",
                arrayField(
                    translations.value.listItem,
                    "items",
                    {
                        text: {
                            label: translations.value.text,
                            type: "text",
                        },
                    },
                    [
                        {
                            text: "Industry-leading compensation",
                        },
                        {
                            text: "Adaptable working schedule",
                        },
                        {
                            text: "Generous paid time off",
                        },
                        {
                            text: "Global team gatherings",
                        },
                        {
                            text: "Comprehensive family benefits",
                        },
                        {
                            text: "Positive company culture",
                        },
                    ],
                    {
                        text: "New item",
                    }
                )
            )
            .withField(
                "link",
                buttonField(
                    translations.value.button,
                    {
                        label: "Explore career opportunities",
                        href: "#",
                    },
                    {
                        label: translations.value.label,
                        href: translations.value.href,
                    }
                )
            )
            .withField(
                "image",
                descriptiveImageField(
                    translations.value.src,
                    {
                        src: getPaceholderImageUrl("800/475", pageColors.value.brand),
                        alt: "Contemporary workspace with greenery and abundant natural lighting",
                    },
                    {
                        src: translations.value.src,
                        alt: translations.value.alt,
                    }
                )
            )
            .withStyleFields([
                colorDefaults.value.background,
                colorDefaults.value.text,
                colorDefaults.value.brand,
                colorDefaults.value.border,
            ])
            .build()
    );

    const twoColumnsWithPhotoRightBlock = computed<BlockDef>(() =>
        createBlock<TwoColumnsWithPhotoRightProps>({
            typeId: "TwoColumnsWithPhotoRight",
            type: "cta",
            name: t("page.blocks.cta.twoColumnsWithPhotoRight"),
            component: TwoColumnsWithPhotoRight,
        })
            .withField(
                "title",
                headingField(translations.value.title, "Join our growing team", "h2", {
                    text: translations.value.text,
                    tag: translations.value.tag,
                })
            )
            .withField(
                "description",
                htmlField(
                    translations.value.description,
                    "We provide exceptional benefits and a supportive workplace designed to nurture your professional journey.",
                    translations.value.description
                )
            )
            .withArrayField(
                "listItems",
                arrayField(
                    translations.value.listItem,
                    "items",
                    {
                        text: {
                            label: translations.value.text,
                            type: "text",
                        },
                    },
                    [
                        {
                            text: "Industry-leading compensation",
                        },
                        {
                            text: "Adaptable working arrangements",
                        },
                        {
                            text: "25 days of annual leave",
                        },
                        {
                            text: "Global company retreats",
                        },
                        {
                            text: "Comprehensive wellness programs",
                        },
                        {
                            text: "Positive team atmosphere",
                        },
                    ],
                    {
                        text: "New item",
                    }
                )
            )
            .withField(
                "link",
                buttonField(
                    translations.value.button,
                    {
                        label: "View open positions",
                        href: "#",
                    },
                    {
                        label: translations.value.label,
                        href: translations.value.href,
                    }
                )
            )
            .withField(
                "image",
                descriptiveImageField(
                    translations.value.src,
                    {
                        src: getPaceholderImageUrl("800/475", pageColors.value.brand),
                        alt: "Contemporary workspace with greenery and natural illumination",
                    },
                    {
                        src: translations.value.src,
                        alt: translations.value.alt,
                    }
                )
            )
            .withStyleFields([
                colorDefaults.value.background,
                colorDefaults.value.text,
                colorDefaults.value.brand,
                colorDefaults.value.border,
            ])
            .build()
    );

    return {
        blocks: computed(() => [
            panelWithAppScreenshotBlock.value,
            centeredOnPanelBlock.value,
            simpleCenteredBlock.value,
            simpleJustifiedBlock.value,
            splitWithImageBlock.value,
            splitWithImageRightBlock.value,
            twoColumnsWithPhotoBlock.value,
            twoColumnsWithPhotoRightBlock.value,
        ]),
    };
}
